import type { PlatformMessage } from '@liks/backend-svc-api/esm/models/index';
import type { MessageSourceDTO } from '@liks/backend-svc-api/esm/models/index';
import type {
  ChatMessage,
  ChatMessageSource,
} from '../../../types/chatMessage';
import type { DirectoryUserWithId } from '../../../types/user';
import { formatDirectoryUserDisplayName } from '../../user/userDisplay';

function normalizeMessageSources(
  sources: MessageSourceDTO[] | null | undefined,
): ChatMessageSource[] | undefined {
  if (!sources || sources.length === 0) {
    return undefined;
  }

  return sources.map((source) => ({
    id: source.id ?? undefined,
    documentId: source.documentId ?? undefined,
    documentName: source.documentName ?? undefined,
    knowledgeBaseId: source.knowledgeBaseId ?? undefined,
    knowledgeBaseName: source.knowledgeBaseName ?? undefined,
    sourceType: source.sourceType ?? undefined,
    sourceUrl: source.sourceUrl ?? undefined,
    sourceTitle: source.sourceTitle ?? undefined,
    trainingExampleId: source.trainingExampleId ?? undefined,
    refStatus: source.refStatus ?? undefined,
  }));
}

export type NormalizePlatformMessagesContext = {
  sessionId: string;
  userId?: string;
  userDisplayName: string;
  defaultUserName: string;
  assistantName: string;
  directoryUserById: Map<string, DirectoryUserWithId>;
};

export function normalizePlatformMessage(
  message: PlatformMessage & { id: string },
  previousMessage: (PlatformMessage & { id: string }) | undefined,
  context: NormalizePlatformMessagesContext,
): ChatMessage {
  const imageFormat =
    message.imageFormat ??
    (message.role === 'ASSISTANT' && previousMessage?.role === 'USER'
      ? previousMessage.imageFormat
      : undefined);
  const imageAspectRatio =
    (message.imageAspectRatio as ChatMessage['imageAspectRatio']) ??
    (message.role === 'ASSISTANT' && previousMessage?.role === 'USER'
      ? (previousMessage.imageAspectRatio as ChatMessage['imageAspectRatio'])
      : undefined);

  return {
    id: message.id,
    sessionId: message.sessionId ?? context.sessionId,
    content: message.content ?? '',
    sentAt: message.stamp ?? message.created ?? new Date(0).toISOString(),
    sources: normalizeMessageSources(message.sources),
    hasAttachment: message.hasAttachment === true,
    imageGenerationFailed: message.imageGenerationFailed === true,
    imageFormat: imageFormat ?? undefined,
    imageAspectRatio: imageAspectRatio ?? undefined,
    author:
      message.role === 'ASSISTANT'
        ? {
            type: 'ai',
            name: context.assistantName,
          }
        : {
            type: 'user',
            id: message.authorUserId ?? '',
            name:
              message.authorUserId === context.userId
                ? context.userDisplayName
                : formatDirectoryUserDisplayName(
                    context.directoryUserById.get(message.authorUserId ?? ''),
                    message.authorUserId ?? context.defaultUserName,
                  ),
          },
  };
}

export function normalizePlatformMessages(
  messages: Array<PlatformMessage & { id: string }>,
  context: NormalizePlatformMessagesContext,
): ChatMessage[] {
  const sortedMessages = [...messages].sort((left, right) => {
    const leftTime = new Date(
      left.stamp ?? left.created ?? new Date(0).toISOString(),
    ).getTime();
    const rightTime = new Date(
      right.stamp ?? right.created ?? new Date(0).toISOString(),
    ).getTime();
    return leftTime - rightTime;
  });

  return sortedMessages.map((message, index, allMessages) =>
    normalizePlatformMessage(
      message,
      index > 0 ? allMessages[index - 1] : undefined,
      context,
    ),
  );
}

export function mergePlatformChatMessages(
  serverMessages: ChatMessage[],
  optimisticMessages: ChatMessage[],
): ChatMessage[] {
  if (optimisticMessages.length === 0) {
    return serverMessages;
  }

  const lastServerMessage = serverMessages[serverMessages.length - 1];
  const remainingOptimisticMessages = optimisticMessages.filter((message) => {
    if (message.author.type === 'user') {
      return !hasMatchingServerUserMessage(serverMessages, message);
    }

    const isLocalAssistantPlaceholder =
      message.isPendingAssistantReply === true ||
      message.isStreamingAssistantReply === true;
    if (!isLocalAssistantPlaceholder) {
      return true;
    }

    // Drop the local pending/streaming bubble only once a *new* assistant
    // message for this turn is on the server (live event / stream merge).
    // A prior-turn AI reply must not hide the spinner.
    if (lastServerMessage?.author.type !== 'ai') {
      return true;
    }

    const lastServerSentAt = new Date(lastServerMessage.sentAt).getTime();
    const optimisticSentAt = new Date(message.sentAt).getTime();
    return (
      Number.isNaN(lastServerSentAt) ||
      Number.isNaN(optimisticSentAt) ||
      lastServerSentAt < optimisticSentAt
    );
  });

  if (remainingOptimisticMessages.length === 0) {
    return serverMessages;
  }

  return [...serverMessages, ...remainingOptimisticMessages];
}

/** Allow small clock skew between optimistic `sentAt` and the persisted stamp. */
const OPTIMISTIC_USER_MATCH_SKEW_MS = 5_000;

function hasMatchingServerUserMessage(
  serverMessages: ChatMessage[],
  optimistic: ChatMessage,
): boolean {
  if (optimistic.author.type !== 'user') {
    return false;
  }

  const optimisticAuthorId = optimistic.author.id;
  const optimisticSentAt = new Date(optimistic.sentAt).getTime();

  // Scan newest-first so an identical prior-turn message does not hide the
  // current optimistic bubble before its server copy arrives.
  for (let index = serverMessages.length - 1; index >= 0; index -= 1) {
    const server = serverMessages[index];
    if (server == null || server.author.type !== 'user') {
      continue;
    }
    if (server.content !== optimistic.content) {
      continue;
    }
    if (
      optimisticAuthorId !== '' &&
      server.author.id !== '' &&
      server.author.id !== optimisticAuthorId
    ) {
      continue;
    }

    const serverSentAt = new Date(server.sentAt).getTime();
    if (Number.isNaN(serverSentAt) || Number.isNaN(optimisticSentAt)) {
      return true;
    }
    if (serverSentAt + OPTIMISTIC_USER_MATCH_SKEW_MS >= optimisticSentAt) {
      return true;
    }

    // Most recent content/author match is from an earlier turn — keep optimistic.
    return false;
  }

  return false;
}
